/**
 * coverRender.js — the unified JSON-cover interpreter (web).
 *
 * A widget's catalog COVER is a small declarative `cover_json` document
 * (size + bg + an ordered list of placed nodes, with data bindings) that BOTH
 * the web (this file → HTML in a sandboxed blob-iframe) and the iPhone app (a
 * native Flutter interpreter) render from the SAME spec. This is the shared
 * contract — keep the node vocabulary in lockstep with the app + the schema
 * lint (`api-server .../widget_schema._COVER_NODE_TYPES`) + widget-design-kit.
 *
 * `resolveCover(manifest, seed, opts)` dispatches:
 *   - manifest.cover_json           → this interpreter (the new scheme)
 *   - cover_type:"html"             → the bundle's hand-authored cover.html (host loads it)
 *   - else (legacy preset/config)   → the UNCHANGED buildDefaultCoverHtml (back-compat)
 *
 * Values may be static, `{bind:"entries[0].name"}` (a dotted data path), or
 * `{agg:{op,of,field?,n?,suffix?}}` (count/sum/latest/recent over the feed).
 * Images are refs — `{file}` / `{media}` (resolved host-side before baking) or
 * `{bind}` (resolved at runtime against __WIDGET_DATA__). NO inline base64 (the
 * manifest rides every catalog response).
 */

import {
  TOKENS_CSS,
  coverTypeOf,
  coverPresetFor,
  coverConfigOf,
  buildDefaultCoverHtml,
} from '../coverDefault.js';

// Node primitives CSS (the archetype vocabulary). Reuses the design tokens from
// TOKENS_CSS; only cover-node layout/visuals live here.
const NODE_CSS = `
#cz{height:100%;}
.cn-root{position:absolute;inset:0;overflow:hidden;--p:14px;background:var(--surface);}
.cn-root.cn-bg-dark{background:#0e0e10;color:#fff;}
.cn-root.cn-bg-paper{background:var(--surface-2);}
.cn-root.cn-bg-light{background:var(--surface);}
.cn-bglayer{position:absolute;inset:0;background-size:cover;background-position:center;}
.cn-bglayer.cn-contain{background-size:contain;background-repeat:no-repeat;}
/* auto-cycling images cross-fade (JS toggles .cn-on); the active layer sits on top. */
.cn-cyc{opacity:0;transition:opacity .6s ease;}
.cn-cyc.cn-on{opacity:1;z-index:1;}
.cn-scrim{position:absolute;inset:0;}
.cn-scrim.cn-scrim-bottom{background:linear-gradient(180deg,rgba(0,0,0,0) 38%,rgba(0,0,0,.72) 100%);}
.cn-scrim.cn-scrim-full{background:rgba(0,0,0,.28);}
/* light top-left wash — a photo anchored right on a light base (My Garden). */
.cn-scrim.cn-scrim-light{background:linear-gradient(135deg,#EDEBE8 0%,rgba(237,235,232,.85) 26%,rgba(237,235,232,.12) 56%,rgba(237,235,232,0) 72%);}
/* region containers — nodes at the same region stack in a flex column */
.cn-region{position:absolute;display:flex;flex-direction:column;gap:6px;max-width:calc(100% - 2*var(--p));z-index:2;}
.cn-r-top-left{top:var(--p);left:var(--p);align-items:flex-start;text-align:left;}
.cn-r-top-right{top:var(--p);right:var(--p);align-items:flex-end;text-align:right;}
.cn-r-bottom-left{bottom:var(--p);left:var(--p);align-items:flex-start;text-align:left;}
.cn-r-bottom-right{bottom:var(--p);right:var(--p);align-items:flex-end;text-align:right;}
/* corner regions can't span the full width or a left title + right chip/fab
   collide — cap them so opposite corners never overlap (and titles wrap like the app). */
.cn-r-top-left,.cn-r-bottom-left{max-width:64%;}
.cn-r-top-right,.cn-r-bottom-right{max-width:34%;}
.cn-r-bottom{left:var(--p);right:var(--p);bottom:var(--p);align-items:stretch;max-width:none;}
.cn-r-center{top:50%;left:50%;transform:translate(-50%,-50%);align-items:center;text-align:center;}
.cn-r-full{inset:0;max-width:none;}
/* text */
.cn-text{font-family:var(--font-display);line-height:1.12;letter-spacing:-.01em;overflow:hidden;
  display:-webkit-box;-webkit-box-orient:vertical;}
.cn-text.cn-upper{text-transform:uppercase;letter-spacing:.08em;}
/* chip / pill / fab */
.cn-chip{display:inline-flex;align-items:center;padding:4px 10px;border-radius:var(--r-pill);
  font-size:11px;font-weight:700;letter-spacing:.02em;white-space:nowrap;background:var(--accent-soft);color:var(--text);}
.cn-chip.cn-tone-soft{background:rgba(0,0,0,.06);}
.cn-chip.cn-tone-dark{background:rgba(0,0,0,.7);color:#fff;}
.cn-pill{display:inline-flex;align-items:center;gap:6px;padding:9px 14px;border-radius:var(--r-pill);
  background:rgba(255,255,255,.9);color:#111;box-shadow:var(--shadow-1);align-self:flex-start;max-width:100%;}
.cn-pill .cn-pill-label{font-size:13px;font-weight:700;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}
.cn-pill .cn-pill-sub{font-size:11px;color:var(--text-3);white-space:nowrap;}
.cn-pill .cn-pill-cta{font-size:14px;font-weight:800;}
.cn-pill.cn-pill-glass{background:rgba(255,255,255,.16);color:#fff;box-shadow:none;-webkit-backdrop-filter:blur(4px);backdrop-filter:blur(4px);}
/* info pill = title over subtitle (matches the app's _WidgetHomeInfoPill), full width */
.cn-pill.cn-pill-info{flex-direction:column;align-items:flex-start;gap:1px;align-self:stretch;max-width:100%;}
.cn-pill.cn-pill-info .cn-pill-label{color:#555;font-size:14px;max-width:100%;}
.cn-pill.cn-pill-info .cn-pill-sub{color:#8B8B8B;font-size:11px;}
.cn-fab{width:34px;height:34px;border-radius:var(--r-pill);display:inline-flex;align-items:center;
  justify-content:center;background:rgba(255,255,255,.92);color:#111;font-size:20px;font-weight:400;
  box-shadow:var(--shadow-1);flex:0 0 auto;overflow:hidden;}
.cn-fab.cn-fab-avatar{background-size:cover;background-position:center;}
.cn-fab.cn-fab-glass{background:rgba(255,255,255,.5);color:#fff;box-shadow:none;}
.cn-fab.cn-fab-dark{background:rgba(255,255,255,.14);color:#fff;box-shadow:none;}
.cn-fab img{width:100%;height:100%;object-fit:cover;}
/* image / strip */
.cn-image{background-position:center;background-repeat:no-repeat;}
.cn-strip{display:flex;gap:6px;width:100%;min-width:150px;}
.cn-strip .cn-cell{flex:1 1 0;aspect-ratio:1;border-radius:8px;background-size:cover;background-position:center;
  background-color:var(--surface-3);min-width:0;}
/* digits — a 5x7 dot-matrix rendered as a self-contained SVG (app renders the
   same SVG via SvgPicture.string → pixel parity, no native dot painter). */
.cn-digits{display:flex;align-items:flex-end;}
.cn-digits svg{display:block;}
.cn-unit{font-size:12px;font-weight:700;color:var(--text-3);margin-left:6px;align-self:flex-end;padding-bottom:2px;white-space:nowrap;}
/* dotgrid — an activity heatgrid */
.cn-dotgrid{display:grid;gap:3px;}
.cn-dotgrid i{aspect-ratio:1;border-radius:50%;}
/* fan — 3 leaning mini-cards */
.cn-fan{display:flex;justify-content:center;align-items:flex-start;padding-top:2px;}
.cn-fan .cn-fc{width:66px;background:var(--surface);border-radius:11px;padding:4px 4px 3px;
  box-shadow:0 3px 10px rgba(0,0,0,.08),inset 0 0 0 .5px var(--line);}
.cn-fan .cn-fc .cn-art{height:50px;border-radius:8px;overflow:hidden;display:flex;align-items:center;
  justify-content:center;background-size:cover;background-position:center;background:var(--accent-soft);
  font-size:24px;color:var(--accent);font-weight:800;}
.cn-fan .cn-fc .cn-flab{display:block;text-align:center;font-size:7px;font-weight:700;letter-spacing:.09em;
  text-transform:uppercase;color:rgba(0,0,0,.35);padding:3px 0 1px;white-space:nowrap;overflow:hidden;}
.cn-fan .cn-fc.cn-a{transform:rotate(-6deg) translateY(6px);}
.cn-fan .cn-fc.cn-b{z-index:2;margin:0 -6px;transform:translateY(-1px) scale(1.06);}
.cn-fan .cn-fc.cn-c{transform:rotate(6deg) translateY(6px);}
`;

// 5x7 dot-matrix font (each glyph = 7 rows of 5 bits, MSB = leftmost column).
const _DM_FONT_SRC = {
  '0': [0x0e, 0x11, 0x13, 0x15, 0x19, 0x11, 0x0e],
  '1': [0x04, 0x0c, 0x04, 0x04, 0x04, 0x04, 0x0e],
  '2': [0x0e, 0x11, 0x01, 0x02, 0x04, 0x08, 0x1f],
  '3': [0x1f, 0x02, 0x04, 0x02, 0x01, 0x11, 0x0e],
  '4': [0x02, 0x06, 0x0a, 0x12, 0x1f, 0x02, 0x02],
  '5': [0x1f, 0x10, 0x1e, 0x01, 0x01, 0x11, 0x0e],
  '6': [0x06, 0x08, 0x10, 0x1e, 0x11, 0x11, 0x0e],
  '7': [0x1f, 0x01, 0x02, 0x04, 0x08, 0x08, 0x08],
  '8': [0x0e, 0x11, 0x11, 0x0e, 0x11, 0x11, 0x0e],
  '9': [0x0e, 0x11, 0x11, 0x0f, 0x01, 0x02, 0x0c],
  ':': [0x00, 0x04, 0x04, 0x00, 0x04, 0x04, 0x00],
  '.': [0x00, 0x00, 0x00, 0x00, 0x00, 0x0c, 0x0c],
  ' ': [0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00],
};

// The runtime interpreter (baked into the iframe). Reads window.__WIDGET_DATA__ +
// __USER_DATA__ + the baked COVER_SPEC / DM_FONT. No backticks / ${} inside.
const RENDER_JS = `
(function(){
  var D = window.__WIDGET_DATA__ || {}; var U = window.__USER_DATA__ || {};
  var ap = U.appearance; if (ap === 'light' || ap === 'dark') document.documentElement.setAttribute('data-theme', ap);
  var SPEC = (typeof COVER_SPEC === 'object' && COVER_SPEC) ? COVER_SPEC : { nodes: [] };
  var FONT = (typeof DM_FONT === 'object' && DM_FONT) ? DM_FONT : {};
  var title = D.title || D.name || U.instance_title || U.title || 'Widget';
  function esc(s){ return String(s==null?'':s).replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/"/g,'&quot;'); }
  function cssUrl(u){ return "url('" + String(u).replace(/'/g,"%27").replace(/&/g,'&amp;') + "')"; }
  function payload(e){ return (e && e.payload) || e || {}; }
  function imgOf(e){ var p=payload(e); var v=Array.isArray(p.images)?p.images[0]:(p.image_url||p.image||p.thumbnail); return v||''; }
  function nameOf(e){ var p=payload(e); return p.name||p.title||p.label||''; }
  function num(x){ var n = typeof x==='number'?x:parseFloat(String(x==null?'':x).replace(/[^0-9.\\-]/g,'')); return isFinite(n)?n:0; }

  // Normalized entries: payload fields flattened + computed name/image, so data
  // binds ("entries[0].name", "entries[].image") and aggs read one friendly shape.
  var rawEntries = Array.isArray(D.entries) ? D.entries : (Array.isArray(D.items) ? D.items : []);
  var entries = rawEntries.map(function(e){ var p=payload(e); var o={};
    for (var k in p){ if (Object.prototype.hasOwnProperty.call(p,k)) o[k]=p[k]; }
    o.name = nameOf(e); o.image = imgOf(e); return o; });
  var DD = {}; for (var dk in D){ if (Object.prototype.hasOwnProperty.call(D,dk)) DD[dk]=D[dk]; }
  DD.entries = entries; DD.title = DD.title || title;

  // dotted-path resolver over DD: "title", "entries[0].name", "entries[].image" (→ array)
  function path(obj, expr){
    if (obj == null || !expr) return undefined;
    var parts = String(expr).split('.'); var cur = obj;
    for (var i=0;i<parts.length;i++){
      var m = parts[i].match(/^([^\\[]*)(\\[(\\d*)\\])?$/); if(!m) return undefined;
      var key = m[1]; if (key) { if (cur==null) return undefined; cur = cur[key]; }
      if (m[2] !== undefined) {
        if (!Array.isArray(cur)) return undefined;
        if (m[3] === '') { var rest = parts.slice(i+1).join('.'); return cur.map(function(x){ return rest ? path(x, rest) : x; }); }
        cur = cur[parseInt(m[3],10)];
      }
    }
    return cur;
  }
  function agg(spec){
    var of = (spec && spec.of) || 'entries';
    var arr = of === 'entries' ? entries : (path(DD, of) || []);
    if (!Array.isArray(arr)) arr = [];
    var op = spec && spec.op;
    if (op === 'count') return arr.length;
    if (op === 'recent') return Math.min(arr.length, spec.n || arr.length);
    if (op === 'sum'){ var s=0; for (var i=0;i<arr.length;i++){ s += num(spec.field ? arr[i][spec.field] : arr[i]); } return s; }
    if (op === 'latest'){ var e0=arr[0]||{}; return spec.field ? e0[spec.field] : (e0.name||''); }
    return arr.length;
  }
  // resolve a node's primary value (value | bind | agg) → display string
  function value(node){
    if (node.value !== undefined && node.value !== null) return String(node.value);
    if (node.bind) { var v = path(DD, node.bind);
      return v==null ? '' : String(Array.isArray(v)?v.filter(Boolean).join(', '):v); }
    if (node.agg) { var a = agg(node.agg); var suf = (node.agg.suffix)||''; return String(a)+suf; }
    return '';
  }
  // resolve an image ref → url ('' if none). {file}/{media} are pre-resolved to
  // {url:…} host-side; {bind} resolves here; a bare string passes through.
  function refUrl(r){
    if (!r) return '';
    if (typeof r === 'string') return r;
    if (r.url) return r.url;
    if (r.bind) { var v = path(DD, r.bind); return Array.isArray(v)?(v[0]||''):(v||''); }
    return '';
  }
  function refList(r, max){
    if (r && r.bind){ var v = path(DD, r.bind); var a = Array.isArray(v)?v:(v?[v]:[]); return a.filter(Boolean).slice(0, max||8); }
    if (Array.isArray(r)) return r.map(refUrl).filter(Boolean).slice(0, max||8);
    var one = refUrl(r); return one ? [one] : [];
  }
  function colorOf(c){
    if (!c) return 'var(--text)';
    var map = { muted:'var(--text-3)', sub:'var(--text-2)', text:'var(--text)', accent:'var(--accent)',
      line:'var(--line)', white:'#fff' };
    return map[c] || c;
  }
  // A 5x7 dot-matrix number rendered as a self-contained inline SVG (circles).
  // The same bitmap font + geometry is reimplemented in the app (a dozen lines)
  // so the two hosts emit identical SVGs. cell = dot pitch in px.
  function matrix(str, onC, offC, cell){
    var c = cell || 6, r = c * 0.42, s = String(str), gW = 5*c, gGap = c;
    var W = s.length ? s.length*gW + (s.length-1)*gGap : 0, H = 7*c;
    var on = onC || 'var(--text)', off = offC || 'rgba(0,0,0,.12)', dots = '';
    for (var k=0;k<s.length;k++){
      var g = FONT[s[k]] || FONT[' '] || [0,0,0,0,0,0,0], ox = k*(gW+gGap);
      for (var row=0;row<7;row++){ for (var col=0;col<5;col++){
        var bit = (g[row] >> (4-col)) & 1;
        dots += '<circle cx="'+(ox+col*c+c/2).toFixed(1)+'" cy="'+(row*c+c/2).toFixed(1)+'" r="'+r.toFixed(2)+'" fill="'+(bit?on:off)+'"/>';
      } }
    }
    return '<svg xmlns="http://www.w3.org/2000/svg" width="'+W+'" height="'+H+'" viewBox="0 0 '+W+' '+H+'">'+dots+'</svg>';
  }

  function renderNode(node){
    var t = node.type;
    if (t === 'text'){
      var st = 'font-size:'+(node.size||16)+'px;font-weight:'+(node.weight||700)+';';
      if (node.color) st += 'color:'+colorOf(node.color)+';';
      if (node.lines) st += '-webkit-line-clamp:'+node.lines+';';
      if (node.w) st += 'max-width:'+node.w+'px;';
      return '<div class="cn-text'+(node.upper?' cn-upper':'')+'" style="'+st+'">'+esc(value(node)).replace(/\\n/g,'<br>')+'</div>';
    }
    if (t === 'chip'){ return '<span class="cn-chip cn-tone-'+(node.tone||'default')+'">'+esc(value(node))+'</span>'; }
    if (t === 'pill'){
      var isInfo = node.sub && !node.cta; // title-over-subtitle info pill (Scan/Outfit)
      var lab = '<span class="cn-pill-label">'+esc(value(node))+'</span>';
      var sub = node.sub ? '<span class="cn-pill-sub">'+esc(node.sub.bind?value({bind:node.sub.bind}):node.sub)+'</span>' : '';
      var cta = node.cta ? '<span class="cn-pill-cta">'+esc(node.cta)+'</span>' : '';
      var pcls = 'cn-pill'+(isInfo?' cn-pill-info':'')+(node.tone?' cn-pill-'+node.tone:'');
      return '<div class="'+pcls+'">'+lab+sub+cta+'</div>';
    }
    if (t === 'fab'){
      var kind = node.kind || 'plus';
      if (kind === 'avatar'){ var au = refUrl(node.avatar); return '<div class="cn-fab cn-fab-avatar"'+(au?' style="background-image:'+cssUrl(au)+'"':'')+'></div>'; }
      var ftone = node.tone ? ' cn-fab-'+node.tone : '';
      if (kind === 'icon'){ var iu = refUrl(node.icon); return '<div class="cn-fab'+ftone+'">'+(iu?'<img src="'+esc(iu)+'">':esc(node.glyph||'•'))+'</div>'; }
      return '<div class="cn-fab'+ftone+'">+</div>';
    }
    if (t === 'image'){ var u = refUrl(node.ref);
      var st2 = (u ? 'background-image:'+cssUrl(u)+';' : 'background-color:var(--surface-3);');
      st2 += node.fit === 'contain' ? 'background-size:contain;' : 'background-size:cover;';
      if (node.w != null) st2 += 'width:'+node.w+'px;'; else st2 += 'width:100%;min-width:140px;';
      if (node.h != null) st2 += 'height:'+node.h+'px;';
      else if (node.aspect) st2 += 'aspect-ratio:'+node.aspect+';'; else st2 += 'aspect-ratio:16/10;';
      if (node.radius != null) st2 += 'border-radius:'+node.radius+'px;';
      if (node.align) st2 += 'background-position:'+node.align+';';
      if (node.rotate) st2 += 'transform:rotate('+node.rotate+'deg);';
      return '<div class="cn-image" style="'+st2+'"></div>'; }
    if (t === 'strip'){ var list = refList(node.refs||node.ref, node.max||4); var minc = node.min||0;
      var cells=''; for (var i=0;i<Math.max(list.length,minc);i++){ var g=list[i]; cells += '<div class="cn-cell"'+(g?' style="background-image:'+cssUrl(g)+'"':'')+'></div>'; }
      return '<div class="cn-strip">'+cells+'</div>'; }
    if (t === 'stack'){ // overlapping images positioned by card coords (rotated docs, an oversized moss); use at:"full"
      var sit = node.items || [], sinner='';
      for (var si=0; si<sit.length; si++){ var it=sit[si]; var iu=refUrl(it.ref); if(!iu) continue;
        var sst='position:absolute;background-image:'+cssUrl(iu)+';background-repeat:no-repeat;background-position:'+(it.align||'center')+';background-size:'+(it.fit==='cover'?'cover':'contain')+';';
        sst+='width:'+(it.w||40)+'px;height:'+(it.h||it.w||40)+'px;left:'+(it.x||0)+'px;top:'+(it.y||0)+'px;';
        if (it.radius!=null) sst+='border-radius:'+it.radius+'px;overflow:hidden;';
        if (it.rotate) sst+='transform:rotate('+it.rotate+'deg);';
        sinner+='<div style="'+sst+'"></div>';
      }
      return '<div style="position:absolute;inset:0">'+sinner+'</div>'; }
    if (t === 'digits'){ var unit = node.unit ? '<span class="cn-unit">'+esc(node.unit)+'</span>' : '';
      return '<div class="cn-digits">'+matrix(value(node), node.on&&colorOf(node.on), node.off&&colorOf(node.off), node.dm)+unit+'</div>'; }
    if (t === 'dotgrid'){ var rows=node.rows||5, cols=node.cols||7, total=rows*cols; var active={};
      if (Array.isArray(node.on_indices)){ // explicit active set (matches a designed pattern)
        for (var oi=0; oi<node.on_indices.length; oi++){ var idx=node.on_indices[oi];
          if (node.major==='col'){ active[(idx%rows)*cols + Math.floor(idx/rows)]=1; } else active[idx]=1; }
      } else { var onN = node.agg ? agg(node.agg) : (node.on_count||0); onN=Math.max(0,Math.min(total,onN));
        for (var k2=0;k2<onN;k2++) active[k2]=1; }
      var onC = colorOf(node.on||'accent'), offC = colorOf(node.off||'line');
      var dots=''; for (var d=0;d<total;d++){ dots += '<i style="background:'+(active[d]?onC:offC)+'"></i>'; }
      var w = node.cell?(node.cell*cols + 3*(cols-1)):Math.min(cols*12, 150);
      return '<div class="cn-dotgrid" style="grid-template-columns:repeat('+cols+',1fr);width:'+w+'px">'+dots+'</div>'; }
    if (t === 'fan'){ var cards = Array.isArray(node.cards)&&node.cards.length ? node.cards.slice(0,3)
        : entries.slice(0,3).map(function(e){ return { label:e.name, ref:{url:e.image} }; });
      while (cards.length<3) cards.push({}); var pos=['cn-a','cn-b','cn-c']; var fan='';
      for (var f=0; f<3; f++){ var cc=cards[f]||{}; var cu=refUrl(cc.ref); var art = cu
          ? '<div class="cn-art" style="background-image:'+cssUrl(cu)+'"></div>'
          : '<div class="cn-art">'+esc(cc.emoji || (String(cc.label||title).charAt(0).toUpperCase()||'•'))+'</div>';
        fan += '<div class="cn-fc '+pos[f]+'">'+art+'<span class="cn-flab">'+esc(cc.label||' ')+'</span></div>'; }
      return '<div class="cn-fan">'+fan+'</div>'; }
    if (t === 'html'){ return node.raw ? String(node.raw) : ''; }
    return '';
  }

  // build root + background, group nodes by region (back→front), render
  var root = document.getElementById('cz'); if (!root) return;
  var bg = SPEC.bg; var rootCls = 'cn-root'; var bgLayers=''; var bgStyle='';
  if (typeof bg === 'string'){
    if (bg==='dark'||bg==='paper'||bg==='light') rootCls += ' cn-bg-'+bg;
    else bgStyle = 'background:'+bg+';';
  } else if (bg && bg.gradient){
    bgStyle = 'background:linear-gradient('+(bg.angle!=null?bg.angle:160)+'deg,'+bg.gradient.join(',')+');';
  } else if (bg && bg.cycle){
    // multiple images that auto-cross-fade on an interval (app filter carousel).
    var cyc = bg.cycle;
    var clist = refList(cyc.bind ? {bind:cyc.bind} : (cyc.refs||cyc), cyc.max||6);
    var cpos = bg.align ? ';background-position:'+bg.align : '';
    var cevery = cyc.every || 3000;
    for (var ci=0; ci<clist.length; ci++){
      bgLayers += '<div class="cn-bglayer cn-cyc'+(ci===0?' cn-on':'')+(bg.fit==='contain'?' cn-contain':'')+'" style="background-image:'+cssUrl(clist[ci])+cpos+'"></div>';
    }
    if (bg.scrim) bgLayers += '<div class="cn-scrim cn-scrim-'+(bg.scrim===true?'full':bg.scrim)+'"></div>';
    rootCls += (bg.scrim === 'light') ? ' cn-bg-paper' : ' cn-bg-dark';
    if (clist.length > 1){
      setTimeout(function(){
        var L = root.querySelectorAll('.cn-cyc'); if (L.length < 2) return; var idx = 0;
        setInterval(function(){ L[idx].classList.remove('cn-on'); idx = (idx+1) % L.length; L[idx].classList.add('cn-on'); }, cevery);
      }, 0);
    }
  } else if (bg && bg.image){
    var bu = refUrl(bg.image);
    var pos = bg.align ? ';background-position:'+bg.align : '';
    if (bu) bgLayers += '<div class="cn-bglayer'+(bg.fit==='contain'?' cn-contain':'')+'" style="background-image:'+cssUrl(bu)+pos+'"></div>';
    if (bg.scrim) bgLayers += '<div class="cn-scrim cn-scrim-'+(bg.scrim===true?'full':bg.scrim)+'"></div>';
    rootCls += (bg.scrim === 'light') ? ' cn-bg-paper' : ' cn-bg-dark';
  }
  var REGIONS = ['full','top-left','top-right','bottom-left','bottom-right','center','bottom'];
  var nodes = Array.isArray(SPEC.nodes) ? SPEC.nodes : [];
  var regionHtml = '';
  for (var ri=0; ri<REGIONS.length; ri++){
    var reg = REGIONS[ri];
    var inReg = nodes.filter(function(n){ return (n.at||'top-left') === reg; });
    if (!inReg.length) continue;
    var inner=''; for (var ni=0; ni<inReg.length; ni++){ inner += renderNode(inReg[ni]); }
    regionHtml += '<div class="cn-region cn-r-'+reg+'">'+inner+'</div>';
  }
  root.innerHTML = '<div class="'+rootCls+'" style="'+bgStyle+'">'+bgLayers+regionHtml+'</div>';
})();
`;

/** Walk a cover_json and rewrite static image refs ({file}/{media}) to {url:…}
 *  host-side (the sandboxed iframe cannot fetch). {bind} refs are left for the
 *  runtime. `opts.resolveFileUrl(name)` / `opts.resolveMediaUrl(handle)` are host
 *  callbacks; absent → the ref resolves empty (renders nothing, never broken). */
function _resolveRefs(node, opts) {
  if (Array.isArray(node)) return node.map((n) => _resolveRefs(n, opts));
  if (!node || typeof node !== 'object') return node;
  if (typeof node.file === 'string') {
    return { url: opts.resolveFileUrl ? opts.resolveFileUrl(node.file) : '' };
  }
  if (typeof node.media === 'string') {
    return { url: opts.resolveMediaUrl ? opts.resolveMediaUrl(node.media) : '' };
  }
  const out = {};
  for (const k of Object.keys(node)) out[k] = _resolveRefs(node[k], opts);
  return out;
}

/**
 * Build a self-contained cover HTML document from a cover_json spec.
 * @param {object} coverJson  the widget's cover_json (v/size/bg/nodes)
 * @param {object} [opts]  { resolveFileUrl(name), resolveMediaUrl(handle) }
 * @returns {string} a standalone HTML doc for the sandboxed cover iframe
 */
export function buildCoverHtml(coverJson, opts = {}) {
  const spec = _resolveRefs(
    coverJson && typeof coverJson === 'object' ? coverJson : { nodes: [] },
    opts,
  );
  return (
    `<!doctype html><html><head><meta charset="utf-8">` +
    `<meta name="viewport" content="width=device-width,initial-scale=1">` +
    `<style>${TOKENS_CSS}${NODE_CSS}${_coverFontCss(opts)}</style></head><body><div id="cz"></div>` +
    `<script>var COVER_SPEC=${JSON.stringify(spec)};var DM_FONT=${JSON.stringify(_DM_FONT_SRC)};</script>` +
    `<script>${RENDER_JS}</script></body></html>`
  );
}

/** MIRROR THE APP: the app renders every cover in SF Pro DISPLAY (bundled
 *  `SFPRODISPLAY*.OTF`, theme `fontFamily:'SFPro'`). The web default (`--font-body`
 *  `-apple-system … "SF Pro Text"`) is the wrong optical variant → different
 *  letterforms + wrap points. Load the SAME OTF files (served from the frontend
 *  origin, browser-cached across covers) and repoint `--font-body`. `font-display:
 *  swap` degrades to the system stack if they're slow/unavailable. `fontBase` opt
 *  overrides the origin (SSR / a CDN); default = the page origin. */
function _coverFontCss(opts) {
  const base =
    (opts && opts.fontBase) ||
    (typeof window !== 'undefined' && window.location ? window.location.origin : '');
  if (!base) return '';
  const face = (w, f) =>
    `@font-face{font-family:'SFProCover';font-weight:${w};font-style:normal;font-display:swap;` +
    `src:url('${base}/fonts/${f}') format('opentype');}`;
  return (
    face(400, 'SFPRODISPLAYREGULAR.OTF') +
    face(500, 'SFPRODISPLAYMEDIUM.OTF') +
    face(700, 'SFPRODISPLAYBOLD.OTF') +
    `:root{--font-body:'SFProCover',-apple-system,BlinkMacSystemFont,"SF Pro Text",system-ui,sans-serif;}`
  );
}

/** True when a manifest carries a valid unified cover_json. */
export function hasCoverJson(manifest) {
  const c = manifest && manifest.cover_json;
  return !!(c && typeof c === 'object' && Array.isArray(c.nodes));
}

/**
 * The one entry point every catalog/use/studio surface calls to build a cover.
 * Dispatches: cover_json (new scheme) → this interpreter; cover_type:"html" →
 * signal the host to load cover.html; else the UNCHANGED legacy preset renderer.
 * @returns {{kind:'html-doc', html:string} | {kind:'cover-file'}}
 */
export function resolveCover(manifest, seed, opts = {}) {
  if (hasCoverJson(manifest)) {
    return { kind: 'html-doc', html: buildCoverHtml(manifest.cover_json, opts) };
  }
  if (coverTypeOf(manifest) === 'html') {
    return { kind: 'cover-file' }; // host serves the bundle's cover.html
  }
  return {
    kind: 'html-doc',
    html: buildDefaultCoverHtml(coverPresetFor(manifest, seed), coverConfigOf(manifest)),
  };
}
